import PropTypes from 'prop-types';
import { overPerBuyerLimit } from '../utils/overPerBuyerLimit';
import React, { useState, useEffect, useMemo, useCallback } from 'react';

import MaskedInput from 'react-text-mask';
import SecondColumn from './SecondColumn';
import pickTotalsTable from '../utils/pickTotalsTable';
import LoginPrompt from './LoginPrompt';
import ConfirmCheckoutHeader from '../../shared/components/ConfirmCheckoutHeader';

import ChooseCheckoutCategories from './ChooseCheckoutCategories';
import CustomCheckoutCategories from './CustomCheckoutCategories';
import ChooseDonation from './ChooseDonation';
import ChooseTicketTypes from './ChooseTicketTypes';
import ChooseSeatedTicketTypes from './ChooseSeatedTicketTypes';
import ChooseAddOns from './ChooseAddOns';
import ChooseSeatedAddOns from './ChooseSeatedAddOns';
import ChooseMembership from './ChooseMembership';
import CalendarEventInterestForm from './CalendarEventInterestForm';
import PoweredByLogo from './PoweredByLogo';
import ChooseResale from './ChooseResale';
import PresaleUnlockPanel from './PresaleUnlockPanel';
import SoldOutTabs, { ADD_ONS_TAB } from './SoldOutTabs';
import LockedAddOns, { LockedAddOnsIntro } from './LockedAddOns';
import CheckoutWaitPanel from './CheckoutWaitPanel';
import CheckoutRetryPanel from './CheckoutRetryPanel';
import useElapsedSeconds from '../../../shared/useElapsedSeconds';
import { cartStartWaitStage, CART_START_WAIT_DETAIL, CART_START_BUSY_TITLE } from '../../../shared/cartStartWait';

import {
  CHOOSING_TICKET_TYPES,
  CHOOSING_ADD_ONS,
  CHOOSING_DONATION,
  CHOOSING_MEMBERSHIP
} from '../constants/webOrderFormConstants';

import { promoCodeMask } from '../../shared/promoCodeValidation';
import isLockedPresale from '../utils/isLockedPresale';
import cartHasResale, { cartIsResaleOnly } from '../utils/cartHasResale';
import { visibleTicketTypes as visibleTicketTypesFor } from '../utils/visibleTicketTypes';
import { resolveActiveCheckoutCategory } from '../utils/checkoutCategorySelection';
import { parentHeader } from '../../../shared/seatsioUtils';
import { listedBesideSeatMap } from '../utils/seatSelection';
import { lockedAddOns } from '../utils/soldOutAddOns';

const discountTargetLabel = (promoCode, ticketTypes, addOns) => {
  const parts = [];

  if (promoCode.applies_to_ticket_types) {
    const ids = promoCode.ticket_type_ids || [];
    const holdIds = promoCode.event_hold_ids || [];
    parts.push(...ids.map(id => ticketTypes.find(t => t.id === id)?.name).filter(Boolean));
    if (holdIds.length > 0) parts.push("members' seats");
    if (ids.length === 0 && holdIds.length === 0 && !promoCode.holds_all_released) parts.push("all tickets");
  }

  if (promoCode.applies_to_add_ons) {
    const ids = promoCode.add_on_ids;
    if (ids && ids.length > 0) {
      parts.push(...ids.map(id => addOns.find(a => a.id === id)?.name).filter(Boolean));
    } else {
      parts.push("all add-ons");
    }
  }

  return parts.length > 0 ? ` for ${parts.join(", ")}` : "";
};


const ticketTypesLoadingStyle = {
  background: "#f9f9f9",
  marginBottom: "15px",
  height: "85px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const canSubmitPromoCodeLookup = (promoCodeName) => {
  return (
    promoCodeName
      && promoCodeName.length > 0
  );
}

export const shouldShowPresaleUnlock = (confirm, promoCode, ticketTypes) => {
  return Boolean(confirm.preselling)
    && Object.keys(promoCode || {}).length === 0
    && (ticketTypes || []).some(isLockedPresale);
};

const showChooseTicketTypesSection = (confirm, ticketTypes, promoCode, visibleTicketTypes) => {
  var ticketTypesAvailableNow = visibleTicketTypes(
      confirm,
      ticketTypes,
      promoCode
    ).filter(tt => !tt.sold_out || tt.sales_paused).length > 0;

  var ticketTypesAvailableLater = (
    !confirm.sold_out
      && ticketTypes.filter(tt => tt.opens_later && !tt.sold_out).length > 0
  );

  return (
    ticketTypesAvailableLater
      || ticketTypesAvailableNow
      || confirm.preselling
  );
}

const offMap = listedBesideSeatMap;
const allOffMapTicketTypes = (confirm, ticketTypes) => ticketTypes.filter(offMap);

export const showOffMapTicketTypesSection = (confirm, ticketTypes, promoCode, offMapVisibleTicketTypes) => (
  !!confirm.seating_chart_id
    && offMapVisibleTicketTypes(confirm, ticketTypes, promoCode).length > 0
);

export const showTicketTypesOrResaleSection = (confirm, ticketTypes, promoCode, visibleTicketTypes, resaleListings) => {
  return (
    showChooseTicketTypesSection(confirm, ticketTypes, promoCode, visibleTicketTypes)
      || (resaleListings || []).length > 0
  );
}

const promoCodeDiscount = (promoCode) => {
  if(promoCode.percentage){
    return (promoCode.percentage + "%");
  } else {
    return formatter.format(promoCode.amount);
  }
}

export const joinWithAnd = (names) => {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

const promoCodeHasDiscount = (promoCode) => (
  parseFloat(promoCode.percentage) > 0 || parseFloat(promoCode.amount) > 0
);

const promoCodeRemainingLimit = (promoCode) => {
  if (promoCode.remaining_capacity == null) return null;
  return promoCode.remaining_capacity;
}

const selectedObjectLabel = (object) => {
  var ownLabel;

  switch(object.objectType) {
  case "Seat":
    ownLabel = "Seat";
    break;
  case "Table":
    ownLabel = "Table";
    break;
  case "GeneralAdmissionArea":
    ownLabel = "Area";
    break;
  default:
    ownLabel = object.labels.displayedLabel;
  }

  var labelParts = [];

  if(object.labels.section){
    labelParts.push("Section " + object.labels.section);
  }

  if(object.labels.parent){
    labelParts.push(parentHeader(object) + " " + object.labels.parent);
  }

  if(object.labels.own){
    labelParts.push(ownLabel + " " + object.labels.own);
  }

  return labelParts.join(", ");
}

const selectedObjectType = (object) => {
  var objectType;

  switch(object.objectType) {
  case "GeneralAdmissionArea":
    objectType = "AddOn";
    break;
  default:
    objectType = object.objectType;
  }

  return objectType;
}

const addOnIsSeated = (addOn) => {
  return (
    addOn
      && addOn.seating_chart_category_key
      && addOn.seating_chart_category_key.length > 0
  )
}

const PickTickets = ({
  team,
  csrfToken,
  confirm,
  widgetType,
  adminPreview,
  ticketTypes,
  promoCode,
  lookupPromoCode,
  promoCodeName,
  promoCodeNameChanged,
  promoCodeNameValid,
  promoCodeInvalidReason,
  promoCodeChanged,
  ticketReservation,
  ticketTypesChanged,
  updateTicketTypeQuantity,
  buildTicketReservation,
  createTicketReservation,
  visibleTicketTypes,
  restrictedTicketTypeId,
  entryTimeLabel,
  seriesReturnUrl,
  visibleAddOns,
  addOns,
  addOnsChanged,
  addOnQuantityOptions,
  ticketReservationChanged,
  userContext,
  cancelCheckout,
  postToParent,
  formatItemsForGoogleTagManager,
  seatsIOPublicKey,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  customerioId,
  parentUrl,
  goToAddOns,
  nextStepIsChooseAddOns,
  choosingTicketTypes,
  isCreatingTicketReservation,
  cartStartBusyMessage,
  cartStartBusyMessageChanged,
  ticketInsurance,
  announceListEnabled,
  waitlistEnabled,
  donationCustomChargeType,
  donationCustomChargeTypeChanged,
  debouncedBuildTicketReservation,
  feeRules,
  nextStepIsChooseDonation,
  goToDonation,
  choosingAddOns,
  showHeader,
  checkoutCategories,
  ticketGroups,
  findTicketGroup,
  ticketTypesLoading,
  startAtAddOns,
  calculator,
  rootUrl,
  onAuthSuccess,
  onPromoCodeRemoved,
  membershipProducts,
  selectedMembershipProduct,
  selectedMembershipProductChanged,
  nextStepIsChooseMembership,
  goToMembership,
  goBack,
  requestedCheckoutCategoryId,
  resaleListings,
  resaleOrderItems,
  resaleQuantityChanged,
  clearPromoRevealedTiers,
  soldOutAddOns,
}) => {
  const [ticketChartSelectionValid, setTicketChartSelectionValid] = useState(true);
  const creatingElapsed = useElapsedSeconds(isCreatingTicketReservation);
  const coveringTickets = isCreatingTicketReservation || Boolean(cartStartBusyMessage);
  const restrictedCategoryId = restrictedTicketTypeId
    ? (checkoutCategories.find((category) =>
        category.checkout_groups.some((group) =>
          group.checkout_group_items.some((item) =>
            item.item_type === 'TicketType' && item.item_id === restrictedTicketTypeId
          )
        )
      )?.id ?? null)
    : null;
  const [activeCategory, setActiveCategory] = useState(() => {
    return resolveActiveCheckoutCategory(checkoutCategories, restrictedCategoryId || requestedCheckoutCategoryId);
  });

  const handleSelectionChange = (isValid) => {
    setTicketChartSelectionValid(isValid);
  };

  const hasResale = cartHasResale(resaleOrderItems);
  const resaleOnly = cartIsResaleOnly(resaleOrderItems, ticketTypes, addOns);
  const hidePromoForResale = resaleOnly && Object.keys(promoCode).length === 0;
  const resaleNotice = (
    <p className="mb-3 text-muted" role="status" aria-live="polite">
      <i className="fas fa-info-circle mr-2" aria-hidden="true"></i>
      Promo codes and membership discounts don&rsquo;t apply to resale tickets.
    </p>
  );
  const offMapVisibleTicketTypes = useCallback(
    (...args) => visibleTicketTypes(...args).filter(offMap),
    [visibleTicketTypes]
  );
  const showOffMapTicketTypes = checkoutCategories.length === 0 && showOffMapTicketTypesSection(confirm, ticketTypes, promoCode, adminPreview ? allOffMapTicketTypes : offMapVisibleTicketTypes);
  const unlockedTicketTypeCount = Object.keys(promoCode).length > 0
    ? visibleTicketTypesFor(confirm, ticketTypes, promoCode).length - visibleTicketTypesFor(confirm, ticketTypes, {}).length
    : 0;

  const ticketQuantity = ticketTypes.reduce((sum, ticketType) => {
    return sum + (ticketType.quantity || 0);
  }, 0);

  const ticketsOverLimit = (
    choosingTicketTypes(ticketReservation)
      && overPerBuyerLimit(ticketQuantity, confirm.max_tickets_per_email)
  );

  const showSoldOutTabs = (
    (soldOutAddOns || []).length > 0
      && confirm.sold_out
      && ticketQuantity === 0
      && choosingAddOns(ticketReservation)
  );

  const standardAddOns = addOns.filter((addOn) => !addOnIsSeated(addOn));
  const seatedAddOns = addOns.filter((addOn) => addOnIsSeated(addOn));

  useEffect(() => {
    setActiveCategory((currentActiveCategory) => {
      const nextActiveCategory = resolveActiveCheckoutCategory(
        checkoutCategories,
        restrictedCategoryId || requestedCheckoutCategoryId,
        currentActiveCategory
      );

      if(currentActiveCategory?.id === nextActiveCategory?.id){
        return currentActiveCategory;
      }

      return nextActiveCategory;
    });
  }, [checkoutCategories, requestedCheckoutCategoryId, restrictedCategoryId]);

  const canSubmitForm = (ticketReservation, ticketTypes, addOns, choosingTicketTypes, isCreatingTicketReservation, ticketChartSelectionValid) => {
    if(isCreatingTicketReservation || !ticketChartSelectionValid){
      return false;
    }

    var hasEnteredDonation = (
      donationCustomChargeType
        && donationCustomChargeType.enteredPrice
        && donationCustomChargeType.enteredPrice.length > 0
    );

    if(hasEnteredDonation && parseFloatFromString(donationCustomChargeType.enteredPrice) <= 0.50){
      return false;
    }

    if(ticketsOverLimit){
      return false;
    }

    if(ticketReservation && ticketReservation.token){
      return (
        ticketTypes.some((tt) => tt.quantity && tt.quantity > 0)
          || addOns.some((ao) => ao.quantity && ao.quantity > 0)
          || hasEnteredDonation
          || hasResale
      );
    }

    if(checkoutCategories.length > 0 && selectedMembershipProduct){
      return true;
    }

    if(choosingTicketTypes(ticketReservation)){
      return (nextStepIsChooseAddOns || nextStepIsChooseDonation || nextStepIsChooseMembership || hasResale);
    } else if(choosingAddOns(ticketReservation)){
      return (nextStepIsChooseDonation || nextStepIsChooseMembership);
    } else if(ticketReservation._screen === CHOOSING_DONATION){
      return nextStepIsChooseMembership;
    } else if(ticketReservation._screen === CHOOSING_MEMBERSHIP){
      return (
        !!selectedMembershipProduct
          || ticketTypes.some((tt) => tt.quantity && tt.quantity > 0)
          || addOns.some((ao) => ao.quantity && ao.quantity > 0)
          || hasEnteredDonation
      );
    } else {
      return false;
    }
  }

  const activeCategoryIsSeated = (activeCategory?.checkout_groups || []).some((group) =>
    (group.checkout_group_items || []).some((item) => item.seatsio_event_id && item.seatsio_event_id.length > 0)
  );
  const onSeatedSeatScreen = choosingTicketTypes(ticketReservation) && (
    (confirm.seating_chart_id && checkoutCategories.length === 0)
      || (checkoutCategories.length > 0 && activeCategoryIsSeated)
  );
  const offMapListBesideMap = onSeatedSeatScreen && showOffMapTicketTypes;
  const canGoBack = !adminPreview && (ticketReservation._screenHistory || []).length > 0;

  const proceedToNextStep = () => {
    if(adminPreview){
      return false;
    }

    if(!canSubmitForm(ticketReservation, ticketTypes, addOns, choosingTicketTypes, isCreatingTicketReservation, ticketChartSelectionValid)){
      return false;
    }

    if(nextStepIsChooseAddOns){
      goToAddOns(ticketReservation, postToParent, ticketReservationChanged);
    } else if(nextStepIsChooseDonation){
      goToDonation();
    } else if(checkoutCategories.length > 0 && checkoutCategories[checkoutCategories.length - 1] !== activeCategory){
      setActiveCategory(checkoutCategories[checkoutCategories.indexOf(activeCategory) + 1]);
    } else if(nextStepIsChooseMembership){
      goToMembership();
    } else {
      const hasAnyTickets = ticketTypes.some((tt) => tt.quantity && tt.quantity > 0);
      const hasAnyAddOns = addOns.some((ao) => ao.quantity && ao.quantity > 0);
      const hasEnteredDonation = (
        donationCustomChargeType
          && donationCustomChargeType.enteredPrice
          && donationCustomChargeType.enteredPrice.length > 0
      );
      const hasMembershipOnly =
        !hasAnyTickets &&
        !hasAnyAddOns &&
        !hasEnteredDonation &&
        !selectedMembershipProduct?.existing_membership &&
        selectedMembershipProduct?.ticketing_price?.id;

      if (hasMembershipOnly) {
        const priceId = selectedMembershipProduct.ticketing_price.id;
        postToParent("opendateOrderGoToCheckout", {});
        window.location.href =
          `${selectedMembershipProduct.confirm_checkout_url}?membership_product_price_id=${priceId}`;
        return;
      }

      postToParent("opendateOrderGoToCheckout", {});
      createTicketReservation(csrfToken, confirm, ticketTypes, promoCode, addOns, ticketReservation, seatsIOChart, customerioId, userContext, parentUrl);
    }
  };

  const promoCodeForm = (
    <form onSubmit={
            (e) => {
              e.preventDefault();

              if(!canSubmitPromoCodeLookup(promoCodeName)){
                return false;
              }

              lookupPromoCode(confirm, promoCodeName, ticketTypes, csrfToken, addOns, ticketReservation, userContext);
            }
          }>
      {!promoCodeNameValid ? (
        <p className="text-danger small mb-0">
          {promoCodeInvalidReason === "not_for_this_show" ? "This code isn't valid for this show." : "Sorry. That code is invalid."}
        </p>
      ) : null}
      <div className="input-group input-group-lg mb-3">
        <MaskedInput
          mask={promoCodeMask()}
          className={"form-control border-right-0 " + (promoCodeNameValid ? "" : "text-danger ") + (confirm.preselling ? "placeholder-text-danger " : "")}
          style={{
            "border": promoCodeNameValid ? "1px solid #f3f3f3" : "1px solid #FF595E",
            "fontSize": "14px"
          }}
          guide={false}
          placeholder={confirm.preselling ? "Enter Pre-Sale Code" : "Add Promo Code"}
          onBlur={() => {}}
          value={promoCodeName || ""}
          onChange={(e) => {
            var newName = e.target.value.toUpperCase();
            promoCodeNameChanged(newName);
          }}
        />
        <div className="input-group-append">
          <button className="btn btn-outline-secondary-dropdown border-left-0"
                  style={{
                    "border": promoCodeNameValid ? "1px solid #f3f3f3" : "1px solid #FF595E",
                    "fontSize": "14px"
                  }}
                  disabled={!canSubmitPromoCodeLookup(promoCodeName)}
                  type="submit">
            <strong>Apply</strong>
          </button>
        </div>
      </div>
    </form>
  );

  return (
  <div>
    {showHeader ? (
      <div className="row">
        <div className="col-12 d-flex justify-content-between align-items-start">
          {confirm.seating_chart_id ? <div /> : (
            <ConfirmCheckoutHeader
              confirm={confirm}
              showTime={checkoutCategories.length === 0}
              entryTimeLabel={entryTimeLabel}
              seriesReturnUrl={seriesReturnUrl}
            />
          )}
          <div className="mt-1">
            <LoginPrompt
              confirmId={confirm.id}
              ticketReservationToken={ticketReservation?.token}
              userContext={userContext}
              onAuthSuccess={onAuthSuccess}
              rootUrl={rootUrl}
            />
          </div>
        </div>
      </div>
    ) : null}
    <div className="row mt-5">
      <div className={`${onSeatedSeatScreen ? "col-12 mb-3" : "col-12 col-md-6 col-lg-8 mb-3"} checkout-wait-host${coveringTickets ? " is-covered" : ""}`}>
        {isCreatingTicketReservation ? (
          <CheckoutWaitPanel {...cartStartWaitStage(creatingElapsed)}
                             detail={CART_START_WAIT_DETAIL} />
        ) : null}
        {!isCreatingTicketReservation && cartStartBusyMessage ? (
          <CheckoutRetryPanel title={CART_START_BUSY_TITLE}
                              message={cartStartBusyMessage}
                              backLabel="Back to tickets"
                              onRetry={proceedToNextStep}
                              onBack={() => cartStartBusyMessageChanged(null)} />
        ) : null}
        {showSoldOutTabs ? (
          <SoldOutTabs activeTab={ADD_ONS_TAB}
                       waitlistEnabled={waitlistEnabled}
                       addOnCount={soldOutAddOns.length}
                       onWaitlistClick={goBack} />
        ) : canGoBack ? (
          <button type="button" className="btn btn-link pl-0 mb-2" data-testid="pick-tickets-back" onClick={goBack}>
            <i className="fas fa-arrow-left mr-2" aria-hidden="true"></i>Back
          </button>
        ) : null}
        {hidePromoForResale ? resaleNotice : Object.keys(promoCode).length > 0 && promoCode._membershipApplied ? (
          <p className="mb-3">
            <i className="fas fa-star mr-2"
               style={{"color": "#98c648"}}></i>
            <strong>{promoCode.membership_product_name || 'Membership'} benefits applied</strong>
            {((promoCode.percentage && parseFloat(promoCode.percentage) > 0) || (promoCode.amount && parseFloat(promoCode.amount) > 0)) && (promoCode.applies_to_ticket_types || promoCode.applies_to_add_ons) && (!promoCode.holds_all_released || discountTargetLabel(promoCode, ticketTypes, addOns) !== "") ? (
              <span>
                {" \u2014 "}
                {promoCodeDiscount(promoCode)} discount
                {discountTargetLabel(promoCode, ticketTypes, addOns)}
                {promoCode.remaining_capacity === 0 ? (
                  " has been fully used for this event. Additional tickets are full price."
                ) : promoCode.remaining_capacity === 1 ? (
                  " is available for one more ticket."
                ) : promoCode.remaining_capacity != null ? (
                  ` is available for up to ${promoCode.remaining_capacity} more tickets.`
                ) : (
                  " is available."
                )}
              </span>
            ) : null}
          </p>
        ) : Object.keys(promoCode).length > 0 && promoCode._passApplied ? (
          <p className="mb-3">
            <i className="fas fa-ticket-alt mr-2"
               style={{"color": "#98c648"}}></i>
            <strong>Your pack is applied</strong>
            {typeof promoCode.remaining === 'number' ? (
              <span>
                {" \u2014 "}
                {promoCode.remaining} of {promoCode.limit} {promoCode.remaining === 1 ? 'credit' : 'credits'} left.
                {" Select your $0 pack ticket below."}
              </span>
            ) : null}
          </p>
        ) : Object.keys(promoCode).length > 0 ? (
          <p className="mb-3" role="status" aria-live="polite">
            <i className="fas fa-check-circle mr-2"
               style={{"color": "#98c648"}}></i>
            <strong>{promoCode.name.toUpperCase()}</strong>
            {!promoCodeHasDiscount(promoCode) ? (
              <span> is applied.{" "}</span>
            ) : promoCode.discounts_only_selected_ticket_types && (promoCode.discounted_ticket_type_names || []).length === 0 ? (
              <span> is applied, but it doesn&rsquo;t discount any tickets for this event.{" "}</span>
            ) : (
              <>
                <span> is applied.</span>
                <span>{" "}
                  {promoCodeDiscount(promoCode)} discount{" "}
                  {promoCode.applies_to_entire_order && promoCodeRemainingLimit(promoCode) === 1 ? (
                    "off one ticket in your order "
                  ) : promoCode.applies_to_entire_order && promoCodeRemainingLimit(promoCode) > 1 ? (
                    `off up to ${promoCodeRemainingLimit(promoCode)} tickets in your order `
                  ) : promoCode.applies_to_entire_order ? (
                    "off your order "
                  ) : promoCode.discounted_tickets_per_order > 1 && (promoCodeRemainingLimit(promoCode) ?? Infinity) > 1 ? (
                    `for up to ${Math.min(promoCode.discounted_tickets_per_order, promoCodeRemainingLimit(promoCode) ?? Infinity)} tickets `
                  ) : promoCode.limit_to_one_ticket_per_order ? (
                    "for one ticket "
                  ) : promoCode.limit_to_one_add_on_per_order ? (
                    "for one add on "
                  ) : promoCodeRemainingLimit(promoCode) === 1 ? (
                    "for one ticket "
                  ) : promoCodeRemainingLimit(promoCode) > 1 ? (
                    `for up to ${promoCodeRemainingLimit(promoCode)} tickets `
                  ) : (
                    "per ticket "
                  )}
                  is available{promoCode.discounts_only_selected_ticket_types && !promoCode.limit_to_one_add_on_per_order ? (
                    ` on ${joinWithAnd(promoCode.discounted_ticket_type_names)}`
                  ) : null}.{" "}
                </span>
              </>
            )}
            {unlockedTicketTypeCount > 0 ? (
              <span>{`${unlockedTicketTypeCount} hidden ticket ${unlockedTicketTypeCount === 1 ? 'type' : 'types'} unlocked. `}</span>
            ) : null}
            <a href="#"
               onClick={
                 (e) => {
                   e.preventDefault();
                   promoCodeChanged({});

                   const { clearedTicketTypes, clearedAddOns } = clearPromoRevealedTiers(calculator, {
                     ticketTypes,
                     addOns,
                     userContext,
                     startAtAddOns
                   });

                   buildTicketReservation(csrfToken, confirm, clearedTicketTypes, {}, clearedAddOns, ticketReservation, userContext);
                   if (onPromoCodeRemoved) onPromoCodeRemoved();
                 }
               }>
              Remove
            </a>
          </p>
        ) : shouldShowPresaleUnlock(confirm, promoCode, ticketTypes) ? (
          <PresaleUnlockPanel
            confirm={confirm}
            csrfToken={csrfToken}
            ticketTypes={ticketTypes}
            promoCodeName={promoCodeName}
            promoCodeNameChanged={promoCodeNameChanged}
            promoCodeNameValid={promoCodeNameValid}
            lookupPromoCode={lookupPromoCode}
            addOns={addOns}
            ticketReservation={ticketReservation}
            userContext={userContext}
            announceListEnabled={announceListEnabled}
          />
        ) : promoCodeForm}
        {hasResale && !hidePromoForResale ? resaleNotice : null}
        {promoCode._membershipApplied && !resaleOnly ? (
          <>
            <p className="small text-muted mb-1">
              Have another code? Applying it replaces your membership discount.
            </p>
            {promoCodeForm}
          </>
        ) : null}
        <form onSubmit={
                (e) => {
                  e.preventDefault();
                  proceedToNextStep();
                }
              }>
          {ticketTypesLoading ? (
            <div className="card border-0" style={ticketTypesLoadingStyle}>Loading...</div>
          ) : checkoutCategories.length > 0 ? (
            (() => {
              const sharedProps = {
                team,
                csrfToken,
                confirm,
                adminPreview,
                ticketTypes,
                promoCode,
                updateTicketTypeQuantity,
                buildTicketReservation,
                userContext,
                addOns,
                ticketReservation,
                postToParent,
                formatItemsForGoogleTagManager,
                visibleTicketTypes,
                announceListEnabled,
                checkoutCategories,
                restrictedTicketTypeId,
                activeCategory,
                setActiveCategory,
                addOnsChanged,
                addOnQuantityOptions,
                visibleAddOns,
                seatedAddOns,
                seatsIOPublicKey,
                selectedObjectLabel,
                selectedObjectType,
                seatsIOChart,
                seatsIOChartChanged,
                seatsIOSelectedObjects,
                seatsIOSelectedObjectsChanged,
                onSelectionChange: handleSelectionChange,
                no_orphan_seats: team.no_orphan_seats,
                ticketTypesChanged,
                donationCustomChargeType,
                donationCustomChargeTypeChanged,
                debouncedBuildTicketReservation,
                feeRules,
                donationTitle: confirm && confirm.donation_title ? confirm.donation_title : "Make a Donation",
                donationCoverProcessingText: confirm && confirm.donation_cover_processing_text ? confirm.donation_cover_processing_text : `I'll cover processing costs so that my full donation goes to ${confirm.organizer_name}`,
                ticketGroups,
                findTicketGroup,
                waitlistEnabled,
                startAtAddOns,
                membershipProducts,
                selectedMembershipProduct,
                selectedMembershipProductChanged,
                onProceed: proceedToNextStep,
                submitting: isCreatingTicketReservation,
                hasResaleListings: (resaleListings || []).length > 0,
              };

              return (
                <>
                  {widgetType === 'custom'
                    ? <CustomCheckoutCategories {...sharedProps} />
                    : <ChooseCheckoutCategories {...sharedProps} />}
                  {choosingTicketTypes(ticketReservation) ? (
                    <ChooseResale resaleListings={resaleListings}
                                  resaleOrderItems={resaleOrderItems}
                                  resaleQuantityChanged={resaleQuantityChanged} />
                  ) : null}
                </>
              );
            })()
          ) : choosingTicketTypes(ticketReservation) ? (
            confirm.seating_chart_id ? (
              <>
                <ChooseSeatedTicketTypes team={team}
                                         csrfToken={csrfToken}
                                         confirm={confirm}
                                         ticketTypes={ticketTypes}
                                         promoCode={promoCode}
                                         ticketTypesChanged={ticketTypesChanged}
                                         buildTicketReservation={buildTicketReservation}
                                         userContext={userContext}
                                         addOns={addOns}
                                         ticketReservation={ticketReservation}
                                         postToParent={postToParent}
                                         formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                         seatsIOPublicKey={seatsIOPublicKey}
                                         seatsIOChart={seatsIOChart}
                                         seatsIOChartChanged={seatsIOChartChanged}
                                         seatsIOSelectedObjects={seatsIOSelectedObjects}
                                         seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
                                         selectedObjectLabel={selectedObjectLabel}
                                         selectedObjectType={selectedObjectType}
                                         visibleTicketTypes={visibleTicketTypes}
                                         onSelectionChange={handleSelectionChange}
                                         announceListEnabled={announceListEnabled}
                                         waitlistEnabled={waitlistEnabled}
                                         no_orphan_seats={team.no_orphan_seats}
                                         createTicketReservation={createTicketReservation}
                                         customerioId={customerioId}
                                         onProceed={proceedToNextStep}
                                         submitting={isCreatingTicketReservation}
                                         parentUrl={parentUrl}
                                         hideTotals={offMapListBesideMap} />
                {showOffMapTicketTypes ? (
                  <ChooseTicketTypes team={team}
                                     csrfToken={csrfToken}
                                     confirm={confirm}
                                     adminPreview={adminPreview}
                                     previewFilter={offMap}
                                     ticketTypes={ticketTypes}
                                     promoCode={promoCode}
                                     updateTicketTypeQuantity={updateTicketTypeQuantity}
                                     buildTicketReservation={buildTicketReservation}
                                     userContext={userContext}
                                     addOns={addOns}
                                     ticketReservation={ticketReservation}
                                     postToParent={postToParent}
                                     formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                     visibleTicketTypes={offMapVisibleTicketTypes}
                                     announceListEnabled={announceListEnabled}
                                     ticketGroups={ticketGroups}
                                     findTicketGroup={findTicketGroup}
                                     waitlistEnabled={waitlistEnabled}
                                     ownsUnavailableState={false} />
                ) : null}
                <ChooseResale resaleListings={resaleListings}
                              resaleOrderItems={resaleOrderItems}
                              resaleQuantityChanged={resaleQuantityChanged} />
              </>
            ) : (
              adminPreview || showTicketTypesOrResaleSection(confirm, ticketTypes, promoCode, visibleTicketTypes, resaleListings) ? (
                <>
                  <ChooseTicketTypes team={team}
                                     csrfToken={csrfToken}
                                     confirm={confirm}
                                     adminPreview={adminPreview}
                                     ticketTypes={ticketTypes}
                                     promoCode={promoCode}
                                     updateTicketTypeQuantity={updateTicketTypeQuantity}
                                     buildTicketReservation={buildTicketReservation}
                                     userContext={userContext}
                                     addOns={addOns}
                                     ticketReservation={ticketReservation}
                                     postToParent={postToParent}
                                     formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                     visibleTicketTypes={visibleTicketTypes}
                                     announceListEnabled={announceListEnabled}
                                     ticketGroups={ticketGroups}
                                     findTicketGroup={findTicketGroup}
                                     waitlistEnabled={waitlistEnabled} />
                  <ChooseResale resaleListings={resaleListings}
                                resaleOrderItems={resaleOrderItems}
                                resaleQuantityChanged={resaleQuantityChanged} />
                </>
              ) : waitlistEnabled ? (
                <CalendarEventInterestForm
                  confirm={confirm}
                  csrfToken={csrfToken}
                  interestType="Waitlist"
                />
              ) : (
                <div className="card border-0 h-100"
                     style={{"background": "#f9f9f9"}}>
                  <div className="card-body d-flex align-items-center justify-content-center">
                    <div className="text-center">
                      <p className="mb-2"
                         style={{"fontSize": "16px"}}>
                        <strong>Sold out!</strong>
                      </p>
                      <p className="mb-0">
                        This event has sold out.
                      </p>
                    </div>
                  </div>
                </div>
              )
            )
          ) : ticketReservation._screen === CHOOSING_ADD_ONS ? (
            <>
              {showSoldOutTabs ? (
                <LockedAddOnsIntro confirm={confirm}
                                   lockedAddOns={lockedAddOns(addOns, visibleAddOns)}
                                   ticketReservation={ticketReservation}
                                   userContext={userContext}
                                   onAuthSuccess={onAuthSuccess}
                                   rootUrl={rootUrl} />
              ) : null}
              {confirm.has_add_ons_intro && confirm.add_ons_intro_no_tags.length > 0 ? (
                <div style={{"marginTop": "30px", "marginBottom": "40px"}}
                    dangerouslySetInnerHTML={{__html: confirm.sanitized_add_ons_intro}}>
                </div>
              ) : null}
              {standardAddOns.length > 0 ? (
                <ChooseAddOns team={team}
                              csrfToken={csrfToken}
                              confirm={confirm}
                              ticketTypes={ticketTypes}
                              addOns={addOns}
                              addOnsChanged={addOnsChanged}
                              addOnQuantityOptions={addOnQuantityOptions}
                              buildTicketReservation={buildTicketReservation}
                              promoCode={promoCode}
                              ticketReservation={ticketReservation}
                              userContext={userContext}
                              postToParent={postToParent}
                              formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                              addOnIsSeated={addOnIsSeated}
                              visibleAddOns={visibleAddOns}
                              startAtAddOns={startAtAddOns} />
              ) : null}
              {seatedAddOns.length > 0 && confirm.add_on_seating_chart_id ? (
                <ChooseSeatedAddOns team={team}
                                    csrfToken={csrfToken}
                                    confirm={confirm}
                                    ticketTypes={ticketTypes}
                                    addOns={addOns}
                                    addOnsChanged={addOnsChanged}
                                    buildTicketReservation={buildTicketReservation}
                                    promoCode={promoCode}
                                    ticketReservation={ticketReservation}
                                    userContext={userContext}
                                    postToParent={postToParent}
                                    formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                    seatsIOPublicKey={seatsIOPublicKey}
                                    selectedObjectLabel={selectedObjectLabel}
                                    selectedObjectType={selectedObjectType}
                                    visibleAddOns={visibleAddOns}
                                    startAtAddOns={startAtAddOns} />
              ) : null}
              {showSoldOutTabs ? (
                <LockedAddOns confirm={confirm}
                              lockedAddOns={lockedAddOns(addOns, visibleAddOns)}
                              ticketReservation={ticketReservation}
                              userContext={userContext}
                              onAuthSuccess={onAuthSuccess}
                              rootUrl={rootUrl} />
              ) : null}
              {confirm.has_add_ons_alert_message && confirm.add_ons_alert_message_no_tags.length > 0 ? (
                <div style={{
                      "marginTop": "24px",
                      "marginBottom": "12px",
                      "padding": "15px 17px",
                      "color": "#BD0006",
                      "background": "#FFDBDC",
                      "fontSize": "16px"
                    }}
                    className="alert alert-danger border-0"
                    role="alert"
                    dangerouslySetInnerHTML={{__html: confirm.sanitized_add_ons_alert_message}}>
                </div>
              ) : null}
            </>
          ) : ticketReservation._screen === CHOOSING_DONATION ? (
            <ChooseDonation team={team}
                            csrfToken={csrfToken}
                            confirm={confirm}
                            buildTicketReservation={buildTicketReservation}
                            donationCustomChargeType={donationCustomChargeType}
                            donationCustomChargeTypeChanged={donationCustomChargeTypeChanged}
                            ticketTypes={ticketTypes}
                            addOns={addOns}
                            promoCode={promoCode}
                            userContext={userContext}
                            debouncedBuildTicketReservation={debouncedBuildTicketReservation}
                            feeRules={feeRules}
                            donationTitle={confirm && confirm.donation_title ? confirm.donation_title : "Make a Donation"}
                            donationCoverProcessingText={confirm && confirm.donation_cover_processing_text ? confirm.donation_cover_processing_text : `I'll cover processing costs so that my full donation goes to ${confirm.organizer_name}`}
                            ticketReservation={ticketReservation} />
          ) : ticketReservation._screen === CHOOSING_MEMBERSHIP ? (
            <ChooseMembership membershipProducts={membershipProducts}
                              selectedMembershipProduct={selectedMembershipProduct}
                              selectedMembershipProductChanged={selectedMembershipProductChanged} />
          ) : null}
          {confirm.seating_chart_id && !offMapListBesideMap ? null : (
            <div className={offMapListBesideMap ? "row" : "row d-md-none"}>
              <div className="col"
                   style={{"marginBottom": "15px"}}>
                {(() => {
                  const TotalsComponent = pickTotalsTable(widgetType, resaleOrderItems);
                  return (
                    <TotalsComponent ticketTypes={ticketTypes}
                                     promoCode={promoCode}
                                     ticketReservation={ticketReservation}
                                     addOns={addOns}
                                     seatsIOSelectedObjects={seatsIOSelectedObjects}
                                     confirm={confirm}
                                     ticketInsurance={ticketInsurance}
                                     selectedMembershipProduct={selectedMembershipProduct}
                                     resaleOrderItems={resaleOrderItems} />
                  );
                })()}
              </div>
            </div>
          )}
          {ticketsOverLimit ? (
            <div className='row'>
              <div className='col-12'>
                <p  className='text-danger'>
                  {`You have exceeded the maximum number of tickets for this event. The maximum tickets for this event, established by the organizer, is ${confirm.max_tickets_per_email} tickets. Please revise your selection.`}
                </p>
              </div>
            </div>
          ) : null}
          <div className="row"
               style={{"paddingTop": "5px"}}>
            <div className="col d-flex align-items-center">
              <p className="mb-0"
                 style={{"fontSize": "14px", "color": "var(--widget-secondary-text-color, #686868)"}}>
                Powered by
                <PoweredByLogo />
              </p>
            </div>
            <div className="col text-right">
              {!choosingTicketTypes(ticketReservation) && !isCreatingTicketReservation ? (
                <a href="#"
                   style={{"marginRight": "30px"}}
                   onClick={
                      (e) => {
                        e.preventDefault();
                        var requestPromises = [];

                        (typeof(seatsio) !== "undefined" && seatsio ? seatsio.charts : []).forEach((chart) => {
                          chart.config.onObjectDeselected = (object) => {};

                          var shouldDeselect = (
                            chart
                              && chart.clearSelection
                              && (
                                chart.selectedObjects.length > 0
                                  || chart.selectedSeats.length > 0
                              )
                          );

                          if (shouldDeselect){
                            requestPromises.push(
                              chart.clearSelection()
                            );
                          }
                        })

                        Promise.all(requestPromises)
                          .then(() => {
                            postToParent("opendateOrderCanceled", {});
                            cancelCheckout();
                          });
                      }
                   }>
                  Cancel
                </a>
              ) : null}
              {onSeatedSeatScreen && !hasResale && !offMapListBesideMap ? null : (
                <button type="submit"
                        disabled={!canSubmitForm(ticketReservation, ticketTypes, addOns, choosingTicketTypes, isCreatingTicketReservation, ticketChartSelectionValid)}
                        className="btn btn-danger">
                  {isCreatingTicketReservation ? (
                    <img src="/uploading-loading.gif"
                         className="mr-1"
                         style={{width: "16px"}} />
                  ) : null}
                  {checkoutCategories.length > 0 ? (
                    <strong>{checkoutCategories[checkoutCategories.length - 1] !== activeCategory ? "Next" : "Checkout"}</strong>
                  ) : (
                    <strong>{(nextStepIsChooseAddOns || nextStepIsChooseDonation || nextStepIsChooseMembership) ? "Continue" : "Checkout"}</strong>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
      {onSeatedSeatScreen ? null : (
        <div className="col-12 col-md-6 col-lg-4 mb-3 order-first order-md-last">
          <SecondColumn confirm={confirm}
                        ticketTypes={ticketTypes}
                        promoCode={promoCode}
                        addOns={addOns}
                        seatsIOSelectedObjects={seatsIOSelectedObjects}
                        ticketInsurance={ticketInsurance}
                        ticketReservation={ticketReservation}
                        selectedMembershipProduct={selectedMembershipProduct}
                        widgetType={widgetType}
                        resaleOrderItems={resaleOrderItems} />
        </div>
      )}
    </div>
  </div>
  );
};

PickTickets.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  lookupPromoCode: PropTypes.func.isRequired,
  promoCodeName: PropTypes.string,
  promoCodeNameChanged: PropTypes.func.isRequired,
  promoCodeNameValid: PropTypes.bool,
  promoCodeInvalidReason: PropTypes.string,
  promoCodeChanged: PropTypes.func.isRequired,
  ticketReservation: PropTypes.object,
  ticketTypesChanged: PropTypes.func.isRequired,
  updateTicketTypeQuantity: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  createTicketReservation: PropTypes.func.isRequired,
  visibleTicketTypes: PropTypes.func.isRequired,
  restrictedTicketTypeId: PropTypes.number,
  entryTimeLabel: PropTypes.string,
  seriesReturnUrl: PropTypes.string,
  visibleAddOns: PropTypes.array,
  addOns: PropTypes.array,
  addOnsChanged: PropTypes.func.isRequired,
  addOnQuantityOptions: PropTypes.func.isRequired,
  ticketReservationChanged: PropTypes.func.isRequired,
  userContext: PropTypes.object,
  cancelCheckout: PropTypes.func.isRequired,
  postToParent: PropTypes.func.isRequired,
  formatItemsForGoogleTagManager: PropTypes.func.isRequired,
  seatsIOPublicKey: PropTypes.string,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func.isRequired,
  customerioId: PropTypes.string,
  parentUrl: PropTypes.string,
  goToAddOns: PropTypes.func.isRequired,
  nextStepIsChooseAddOns: PropTypes.bool,
  choosingTicketTypes: PropTypes.func.isRequired,
  isCreatingTicketReservation: PropTypes.bool,
  cartStartBusyMessage: PropTypes.string,
  cartStartBusyMessageChanged: PropTypes.func,
  ticketInsurance: PropTypes.object,
  donationCustomChargeType: PropTypes.object,
  donationCustomChargeTypeChanged: PropTypes.func.isRequired,
  debouncedBuildTicketReservation: PropTypes.func,
  feeRules: PropTypes.array.isRequired,
  nextStepIsChooseDonation: PropTypes.bool,
  goToDonation: PropTypes.func.isRequired,
  choosingAddOns: PropTypes.func.isRequired,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  showHeader: PropTypes.bool,
  checkoutCategories: PropTypes.array,
  ticketGroups: PropTypes.object.isRequired,
  findTicketGroup: PropTypes.func.isRequired,
  ticketTypesLoading: PropTypes.bool.isRequired,
  startAtAddOns: PropTypes.bool,
  calculator: PropTypes.object,
  rootUrl: PropTypes.string,
  onAuthSuccess: PropTypes.func,
  onPromoCodeRemoved: PropTypes.func,
  membershipProducts: PropTypes.array,
  selectedMembershipProduct: PropTypes.object,
  selectedMembershipProductChanged: PropTypes.func,
  nextStepIsChooseMembership: PropTypes.bool,
  goToMembership: PropTypes.func,
  goBack: PropTypes.func,
  requestedCheckoutCategoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  resaleListings: PropTypes.array,
  resaleOrderItems: PropTypes.array,
  resaleQuantityChanged: PropTypes.func,
  clearPromoRevealedTiers: PropTypes.func.isRequired,
  soldOutAddOns: PropTypes.array,
};

PickTickets.defaultProps = {
  promoCodeInvalidReason: null,
};

export default PickTickets;
